import * as ActionType from './action-types';
import axios from 'axios';
import { notification } from 'antd';

axios.defaults.headers.post['Content-Type'] = 'application/json';

// Normalize email at every point it leaves the client: mobile autocapitalize
// and copy-paste from email clients add invisible whitespace and capitalization
// that the backend may treat as a different identity ("registered as
// Foo@Example.com but cannot sign in as foo@example.com").
export const normalizeEmail = (email) => email.trim().toLowerCase();

const SIGN_IN_UNAVAILABLE = 'Sign-in is unavailable right now. Please try again.';

// No HTTP response at all — the request never completed a round trip. That is a
// connectivity failure rather than a server failure, and on school networks the
// most common cause is a web filter blocking our domain: the page itself loads
// (the client domain is allowed), then every API call is dropped. Naming that
// possibility gives the user something actionable; the generic "try again"
// message sends them into a retry loop that cannot succeed.
const SIGN_IN_UNREACHABLE = 'Cannot reach the sign-in server.';
const SIGN_IN_UNREACHABLE_DETAIL =
  'Check your internet connection. If you are on a school or district network, ' +
  'ask your network administrator to allow schoolhouseassessments.com.';

const notifySignInError = (error) => {
  if (!error || !error.response) {
    notification.error({
      message: SIGN_IN_UNREACHABLE,
      description: SIGN_IN_UNREACHABLE_DETAIL
    });
    return;
  }
  const { status, data } = error.response;
  if (status === 401) {
    notification.error({ message: 'Email or password incorrect.' });
    return;
  }
  if (status >= 500) {
    notification.error({ message: SIGN_IN_UNAVAILABLE });
    return;
  }
  if (status === 400 && data && typeof data === 'object' && data.message) {
    notification.error({ message: data.message });
    return;
  }
  notification.error({ message: 'Sign-in failed. Please try again.' });
};

export const createUser = (email, kind, password) => (dispatch) => {
  // Reset just the auth fields here; no session to revoke server-side
  // when registering a new user.
  dispatch({ type: ActionType.logout });

  const data = {
    email: normalizeEmail(email),
    kind: kind,
    password: password
  };

  return axios
    .post(process.env.REACT_APP_API_URL + '/user/create', data, {
      withCredentials: true
    })
    .then((response) => {
      // check to make sure a data object was returned
      if (response.data == null) throw new Error('no response data');

      dispatch(setToken(response.data.user.token));
      // Forward-compatible: when wici-api mirrors /user/login's response shape
      // and Set-Cookie behavior on /user/create, the new user lands with a
      // csrfToken in state and a refreshToken cookie set, so the first
      // access-JWT expiry doesn't silently log them out.
      if (response.data.user.csrfToken) {
        dispatch(setCsrfToken(response.data.user.csrfToken));
      }
      dispatch(getUserInfo(response.data.user.token));
    })
    .catch((error) => {
      console.log(error);
    });
};

export const getUserInfo = (token) => (dispatch) => {
  const data = {};

  const config = {
    headers: {
      Authorization: 'Token ' + token
    }
  };

  return axios
    .post(process.env.REACT_APP_API_URL + '/user/info', data, config)
    .then((response) => {
      dispatch({
        type: ActionType.getUserInfo,
        userInfo: response.data
      });
    })
    .catch((error) => {
      console.log(error);
    });
};

export const login = (email, password) => (dispatch) => {
  // Reset just the auth fields here; we don't want to call /auth/logout
  // before login because there is no session to revoke yet.
  dispatch({ type: ActionType.logout });

  const data = {
    email: normalizeEmail(email),
    password: password
  };

  return axios
    .post(process.env.REACT_APP_API_URL + '/user/login', data, {
      timeout: 15000,
      withCredentials: true
    })
    .then((response) => {
      // check to make sure a data object was returned
      if (response.data == null) throw new Error('no response data');

      dispatch(setToken(response.data.token));
      if (response.data.csrfToken) {
        dispatch(setCsrfToken(response.data.csrfToken));
      }
      dispatch(getUserInfo(response.data.token));
    })
    .catch((error) => {
      notifySignInError(error);
    });
};

export const refreshAuth = () => async (dispatch, getState) => {
  const csrfToken = getState().csrfToken;
  // Skip the doomed round-trip when we have no CSRF token to send (e.g. a
  // returning user just migrated to v2 with an empty csrfToken). The
  // middleware's catch will fall through to the logout flow.
  if (!csrfToken) {
    throw new Error('refreshAuth: no csrfToken in state, cannot refresh');
  }
  const response = await axios.post(
    process.env.REACT_APP_API_URL + '/auth/refresh',
    {},
    {
      headers: { 'X-CSRF-Token': csrfToken },
      withCredentials: true
    }
  );
  dispatch(setToken(response.data.token));
  if (response.data.csrfToken) {
    dispatch(setCsrfToken(response.data.csrfToken));
  }
  return response.data.token;
};

export const logout = () => async (dispatch) => {
  // Clear local state synchronously FIRST so any UI subscribed to the
  // store sees the logged-out state immediately. If we waited on the
  // server round-trip first, callers like dashboard-nav (which navigates
  // to '/' as soon as logout returns control) would mount LandingPage
  // while userInfo.kind was still set, and LandingPage's role-redirect
  // would bounce the user right back to the dashboard.
  dispatch({
    type: ActionType.logout
  });
  // Best-effort: tell the server to revoke the refresh token. Swallow
  // errors so the user is never left in a stale "logged in" UI just
  // because the network is offline.
  try {
    await axios.post(process.env.REACT_APP_API_URL + '/auth/logout', {}, { withCredentials: true });
  } catch (e) {
    // swallow — server-side revoke is best-effort
  }
};

export const setSessionID = (sessionID) => ({
  type: ActionType.setSessionID,
  sessionID: sessionID
});

export const setToken = (token) => ({
  type: ActionType.setToken,
  token: token
});

export const setCsrfToken = (csrfToken) => ({
  type: ActionType.setCsrfToken,
  csrfToken: csrfToken
});
